import { state } from "../state";
import { apiCall } from "../api";
import { showToast, formatBoolean } from "../utils";
import { createVirtualTable, getDisplayColumnName, getSingleColumns, filterSingleColumns } from "./common";

function getEl<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}
function getInput(id: string): HTMLInputElement | null {
  return getEl<HTMLInputElement>(id);
}
function getSelect(id: string): HTMLSelectElement | null {
  return getEl<HTMLSelectElement>(id);
}
function getButton(id: string): HTMLButtonElement | null {
  return getEl<HTMLButtonElement>(id);
}

const Papa = (window as any).Papa;
const XLSX = (window as any).XLSX;

async function renderSingleExportColumnsPanel(data: any[]) {
  const panel = getEl<HTMLElement>("single-export-columns-panel");
  const list = getEl<HTMLElement>("single-export-columns-list");
  const toggle = getButton("single-export-columns-toggle");
  if (!panel || !list || !toggle || !data.length) return;
  const keys = Object.keys(data[0] || {});
  const res = await apiCall('/api/admin/export_columns?scope=single', 'GET');
  const cfg: any = res?.config || { exclude: ['srch_key'], include: null };
  let included = new Set<string>();
  if (cfg.include && cfg.include.length) {
    included = new Set(cfg.include);
  } else {
    const exclude = new Set(cfg.exclude || []);
    keys.forEach(k => { if (!exclude.has(k)) included.add(k); });
  }
  let html = '';
  keys.forEach(k => {
    if (k === 'srch_key') return;
    const checked = included.has(k) ? 'checked' : '';
    html += `<label style="display:flex; align-items:center; gap:6px; cursor:pointer; background:#fff; padding:6px 12px; border-radius:20px; border:1px solid #e0e0e0; font-size:13px;">
      <input type="checkbox" value="${k}" data-single-export-col ${checked}> ${getDisplayColumnName(k)}
    </label>`;
  });
  list.innerHTML = html;
  toggle.onclick = () => {
    panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  };
}

// -------------------- ОДИНОЧНЫЙ РЕЖИМ --------------------
document.getElementById("single-parse")!.onclick = async () => {
  const text = getInput("single-text")!.value;

  if (!text) return;
  // Очищаем предыдущие результаты
  getEl("single-result-card")!.style.display = "none";
  getEl("single-result-table")!.innerHTML = "";
  state.singleSearchResult = null;
  state.singleText = text;
  const groupMapping: Record<string, string> = {
    "Кабели и проводники": "cable",
    "Трубопроводная арматура": "valve"
  };

  const data = await apiCall("/api/parse_single", "POST", { text });
  if (data && data.success) {
    state.singleParsed = data.attributes;
    const rawGroup = data.group;
    state.singleGroup = groupMapping[rawGroup] || rawGroup;
    await renderSingleParsedAttributesReadonly();
    getButton("single-configure-search")!.disabled = false;
    getButton("single-search")!.disabled = false;
  } else {
    getEl("single-parsed-attributes")!.innerHTML = `<div class="error">Ошибка: ${data?.error || "неизвестная"}</div>`;
  }
};

async function renderSingleParsedAttributesReadonly() {
  const group = state.singleGroup;
  const parsed = state.singleParsed;
  if (!group || !parsed) return;
  const attrData = await apiCall(`/api/attributes/group/${encodeURIComponent(group)}`, "GET");
  const groupAttrs = attrData.attributes || [];
  const attrByKey: Record<string, any> = {};
  groupAttrs.forEach((a: any) => { attrByKey[a.key.toLowerCase()] = a; });
  const priority = ["group", "class", "subclass"];
  const entries = Object.entries(parsed).filter(([_, val]) => val !== null && val !== undefined && val !== "") as [string, any][];
  entries.sort((a, b) => {
    const ia = priority.indexOf(a[0]), ib = priority.indexOf(b[0]);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return 0;
  });
  let html = "<div><div><b>Распознанные атрибуты:</b></div><div style=\"margin-top:8px; padding-left:16px;\">";
  for (const [key, val] of entries) {
    let displayVal: any = val;
    if (typeof val === "boolean") displayVal = val ? "да" : "нет";
    const attrInfo = attrByKey[key.toLowerCase()];
    const label = attrInfo ? (attrInfo.label || attrInfo.key) : getDisplayColumnName(key);
    html += `<div style="margin-bottom:4px;"><strong>${label}:</strong> ${displayVal}</div>`;
  }
  html += "</div></div>";
  getEl("single-parsed-attributes")!.innerHTML = html;
}

function _renderAttrInput(
  key: string,
  val: any,
  attrInfo: any,
  strategyAttrs: Set<string>,
  isChecked = false
): string {
  let displayVal: any = val;
  if (typeof val === "boolean") displayVal = val ? "да" : "нет";
  const isBool = typeof val === "boolean";
  const attrType = isBool ? "boolean" : (attrInfo ? attrInfo.type : "categorical");
  const label = attrInfo ? (attrInfo.label || attrInfo.key) : getDisplayColumnName(key);
  const inStrategy = strategyAttrs.has(key);
  const badge = inStrategy
    ? ' <span style="font-size:11px; color:#0070BA; background:#e6f0f8; padding:2px 6px; border-radius:4px; margin-left:4px;">стратегия</span>'
    : "";
  let inputHtml = "";
  if (attrType === "boolean") {
    const v = val === true || String(val).toLowerCase() === "да" ? "да" : val === false || String(val).toLowerCase() === "нет" ? "нет" : "";
    inputHtml = `<select data-single-attr="${key}" style="min-width:120px;"><option value=""></option><option value="да" ${v === "да" ? "selected" : ""}>да</option><option value="нет" ${v === "нет" ? "selected" : ""}>нет</option></select>`;
  } else if (attrType === "numeric") {
    const v = val !== undefined && val !== null && !isNaN(val) ? val : "";
    inputHtml = `<div style="display:flex; align-items:center; gap:6px;">от <input type="number" data-single-attr-min="${key}" value="${v}" style="width:80px;"> до <input type="number" data-single-attr-max="${key}" value="${v}" style="width:80px;"></div>`;
  } else if (attrType === "categorical") {
    inputHtml = `<select data-single-attr="${key}" style="min-width:180px; max-width:300px;" multiple size="3"></select>`;
  } else {
    const v = val !== undefined && val !== null ? val : "";
    inputHtml = `<input type="text" data-single-attr="${key}" value="${v}" style="width:200px;">`;
  }
  const checkedAttr = isChecked ? "checked" : "";
  return `<div style="margin-bottom:6px; display:grid; grid-template-columns: 24px minmax(160px, max-content) 1fr; gap:8px; align-items:start;"><input type="checkbox" data-single-attr-check="${key}" ${checkedAttr} style="justify-self:start;" title="Использовать в подборе"><span style="word-break:break-word;"><strong>${label}</strong>${badge}</span><div style="justify-self:start;">${inputHtml}</div></div>`;
}

async function renderSingleParsedAttributesEditable() {
  state.singleEditedRequest = null;
  const group = state.singleGroup;
  const parsed = state.singleParsed;
  if (!group || !parsed) return;
  const [attrData, preset] = await Promise.all([
    apiCall(`/api/attributes/group/${encodeURIComponent(group)}`, "GET"),
    (async () => {
      const mode = getSelect("single-strategy-mode")!.value;
      if (mode === "custom") {
        const us = state.strategies[group] || {};
        return us.config || {};
      }
      const p = await apiCall(`/api/strategies/preset/${group}/${mode}`, "GET");
      return p.config || {};
    })()
  ]);
  state.singleStrategyConfig = preset;
  const strategyAttrs = new Set<string>(Object.keys(preset));
  const groupAttrs: any[] = attrData.attributes || [];
  const attrByKey: Record<string, any> = {};
  groupAttrs.forEach((a: any) => { attrByKey[a.key.toLowerCase()] = a; });
  const priority = ["group", "class", "subclass"];
  const entries = Object.entries(parsed).filter(([_, val]) => val !== null && val !== undefined && val !== "") as [string, any][];
  entries.sort((a, b) => {
    const ia = priority.indexOf(a[0]), ib = priority.indexOf(b[0]);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return 0;
  });
  const strategyEntries = entries.filter(([key]) => strategyAttrs.has(key));
  const otherEntries = entries.filter(([key]) => !strategyAttrs.has(key));

  let html = "<div><div><b>Настройка параметров поиска</b></div><div style=\"margin-top:8px; padding-left:16px;\">";

  if (strategyEntries.length) {
    html += '<div style="font-size:13px; color:#0070BA; margin-bottom:8px;">🔍 Атрибуты, задействованные в стратегии</div>';
    for (const [key, val] of strategyEntries) {
      html += _renderAttrInput(key, val, attrByKey[key.toLowerCase()], strategyAttrs, true);
    }
  }
  if (otherEntries.length) {
    if (strategyEntries.length) {
      html += '<div style="margin:12px 0; border-top:1px solid #ddd;"></div>';
    }
    html += '<div style="font-size:13px; color:#666; margin-bottom:8px;">📝 Другие атрибуты из записи</div>';
    for (const [key, val] of otherEntries) {
      html += _renderAttrInput(key, val, attrByKey[key.toLowerCase()], strategyAttrs, false);
    }
  }
  html += `<button id="single-extra-attrs-btn" class="secondary" style="margin-top:12px;">Задать дополнительные атрибуты</button>`;
  html += '<div id="single-extra-attrs" style="display:none; margin-top:12px; padding-left:16px;"></div>';
  html += '<div style="margin-top:12px;"><button id="single-apply-search">Применить настройки</button></div>';
  html += "</div></div>";
  getEl("single-parsed-attributes")!.innerHTML = html;

  // Load values for categorical selects
  for (const [key, val] of entries) {
    const attrInfo = attrByKey[key.toLowerCase()];
    if (!attrInfo || attrInfo.type !== "categorical") continue;
    const select = document.querySelector<HTMLSelectElement>(`select[data-single-attr="${key}"]`);
    if (!select) continue;
    const valuesData = await apiCall(`/api/attr_values?group=${encodeURIComponent(group)}&attr=${encodeURIComponent(attrInfo.key)}&req_value=${encodeURIComponent(val)}`, "GET");
    const values: string[] = valuesData.values || [];
    select.innerHTML = values.map((v) => `<option value="${v}" ${String(v) === String(val) ? "selected" : ""}>${v}</option>`).join("");
  }

  // Apply button
  getEl("single-apply-search")!.onclick = () => {
    const checkedAttrs = new Set<string>();
    document.querySelectorAll<HTMLInputElement>("[data-single-attr-check]:checked").forEach((cb) => {
      checkedAttrs.add(cb.dataset.singleAttrCheck!);
    });
    const editedRequest: any = {};
    for (const key of checkedAttrs) {
      const input = document.querySelector<HTMLElement>(`[data-single-attr="${key}"]`);
      const minInput = document.querySelector<HTMLInputElement>(`[data-single-attr-min="${key}"]`);
      const maxInput = document.querySelector<HTMLInputElement>(`[data-single-attr-max="${key}"]`);
      if (minInput && maxInput) {
        const minVal = minInput.value;
        const maxVal = maxInput.value;
        if (minVal !== "" || maxVal !== "") {
          editedRequest[key] = {};
          if (minVal !== "") editedRequest[key].min = parseFloat(minVal);
          if (maxVal !== "") editedRequest[key].max = parseFloat(maxVal);
        }
      } else if (input) {
        if (input.tagName === "SELECT" && (input as HTMLSelectElement).multiple) {
          editedRequest[key] = Array.from((input as HTMLSelectElement).selectedOptions).map((o) => o.value);
        } else {
          editedRequest[key] = (input as HTMLInputElement).value;
        }
      }
    }
    // Always include class/subclass/group if present in parsed
    const parsed = state.singleParsed || {};
    for (const key of ["group", "class", "subclass"]) {
      if (parsed[key] !== undefined && parsed[key] !== null && parsed[key] !== "") {
        if (!editedRequest.hasOwnProperty(key)) editedRequest[key] = parsed[key];
      }
    }
    state.singleEditedRequest = editedRequest;
    showToast("Настройки поиска применены", "success");
  };

  // Extra attrs button
  getEl("single-extra-attrs-btn")!.onclick = async () => {
    const extraContainer = getEl("single-extra-attrs")!;
    if (extraContainer.style.display !== "none") {
      extraContainer.style.display = "none";
      return;
    }
    let extraHtml = '<div style="font-size:13px; color:#666; margin-bottom:8px;">➕ Дополнительные атрибуты группы</div>';
    for (const attr of groupAttrs) {
      const key = attr.key;
      if (parsed.hasOwnProperty(key)) continue;
      extraHtml += _renderAttrInput(key, undefined, attr, strategyAttrs, false);
    }
    extraContainer.innerHTML = extraHtml;
    extraContainer.style.display = "block";
    // Load categorical values for extra attrs
    for (const attr of groupAttrs) {
      if (parsed.hasOwnProperty(attr.key) || attr.type !== "categorical") continue;
      const select = document.querySelector<HTMLSelectElement>(`select[data-single-attr="${attr.key}"]`);
      if (!select) continue;
      const valuesData = await apiCall(`/api/attr_values?group=${encodeURIComponent(group)}&attr=${encodeURIComponent(attr.key)}`, "GET");
      const values: string[] = valuesData.values || [];
      select.innerHTML = values.map((v) => `<option value="${v}">${v}</option>`).join("");
    }
  };
}

document.getElementById("single-configure-search")!.onclick = async () => {
  if (!state.singleParsed) return;
  await renderSingleParsedAttributesEditable();
};

document.getElementById("single-search")!.onclick = async () => {
  if (!state.singleParsed) return;
  let editedRequest: any = {};
  let strategy: any = {};
  let strategyMode = getSelect("single-strategy-mode")!.value;
  const hasCheckboxes = document.querySelectorAll<HTMLInputElement>("[data-single-attr-check]").length > 0;

  if (!hasCheckboxes) {
    // Readonly view: use all parsed attrs and fetch preset directly
    editedRequest = { ...state.singleParsed };
    if (strategyMode === "custom") {
      const userStrat = state.strategies[state.singleGroup!] || {};
      strategy = { [userStrat.mode || "custom"]: userStrat.config || {} };
    } else {
      const preset = await apiCall(`/api/strategies/preset/${state.singleGroup}/${strategyMode}`, "GET");
      strategy = { [strategyMode]: preset.config || {} };
    }
  } else {
    // Editable view: use applied settings
    if (!state.singleEditedRequest) {
      showToast("Сначала примените настройки поиска", "warning");
      return;
    }
    editedRequest = { ...state.singleEditedRequest };
    const checkedAttrs = new Set<string>(Object.keys(editedRequest).filter((k) => !["group", "class", "subclass"].includes(k)));

    // Always include class/subclass/group in request
    (["group", "class", "subclass"] as const).forEach((sysKey) => {
      if (state.singleParsed[sysKey] !== undefined && state.singleParsed[sysKey] !== null && !editedRequest.hasOwnProperty(sysKey)) {
        editedRequest[sysKey] = state.singleParsed[sysKey];
      }
    });

    // Strategy handling
    const strategyConfig = state.singleStrategyConfig || {};
    const strategyAttrs = new Set<string>(Object.keys(strategyConfig));
    const hasExtra = [...checkedAttrs].some((a) => !strategyAttrs.has(a));
    const hasMissing = [...strategyAttrs].some((a) => !checkedAttrs.has(a));
    if ((hasExtra || hasMissing) && strategyMode !== "custom") {
      strategyMode = "custom";
      getSelect("single-strategy-mode")!.value = "custom";
      showToast("Изменён состав атрибутов — поиск переключён на пользовательскую стратегию", "info");
    }

    if (strategyMode === "custom") {
      const customConfig: any = {};
      for (const attr of checkedAttrs) {
        customConfig[attr] = "=";
      }
      strategy = { custom: customConfig };
    } else {
      const filteredPreset: any = {};
      for (const attr of checkedAttrs) {
        if (strategyConfig.hasOwnProperty(attr)) {
          filteredPreset[attr] = strategyConfig[attr];
        }
      }
      strategy = { [strategyMode]: filteredPreset };
    }
  }

  const result = await apiCall("/api/search_single", "POST", {
    title_full: state.singleText,
    request: editedRequest,
    group: state.singleGroup,
    strategy,
    strategy_mode: strategyMode,
    strategies: state.strategies,
    return_columns: null
  });
  if (result && result.data) {
    state.singleResult = result.data;
    getEl("single-result-card")!.style.display = "block";
    await getSingleColumns();
    let cols = filterSingleColumns(Object.keys(result.data[0] || {}));
    if (!cols.length) cols = Object.keys(result.data[0] || {}).filter((c: string) => c !== "srch_key");
    await createVirtualTable("single-result-table", result.data, cols, { editable: false });
    await renderSingleExportColumnsPanel(result.data);

    const buildSingleExportData = () => {
      const panel = getEl<HTMLElement>("single-export-columns-panel");
      let keys = Object.keys(result.data[0] || {}).filter((k) => k !== "srch_key");
      if (panel && panel.style.display !== "none") {
        const checked = Array.from(
          panel.querySelectorAll<HTMLInputElement>("input[data-single-export-col]:checked")
        ).map((cb) => cb.value);
        if (checked.length) keys = checked;
      }
      return result.data.map((row: any) => {
        const clean: Record<string, any> = {};
        for (const k of keys) {
          clean[getDisplayColumnName(k)] = formatBoolean(row[k]);
        }
        return clean;
      });
    };

    getEl("download-single-result")!.onclick = () => {
      const csv = Papa.unparse(buildSingleExportData());
      const blob = new Blob([csv], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "single_result.csv";
      a.click();
    };
    getEl("download-single-xlsx")!.onclick = () => {
      const ws = XLSX.utils.json_to_sheet(buildSingleExportData());
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Results");
      XLSX.writeFile(wb, "single_result.xlsx");
    };
  } else {
    showToast(result?.message || "Аналоги не найдены", "info");
  }
};

document.getElementById("clear-single-results")!.onclick = () => {
  getEl("single-result-card")!.style.display = "none";
  getEl("single-result-table")!.innerHTML = "";
  state.singleSearchResult = null;
  state.singleParsed = null;
  state.singleGroup = null;
  getEl("single-parsed-attributes")!.innerHTML = "";
  getButton("single-configure-search")!.disabled = true;
  getButton("single-search")!.disabled = true;
};
